import type { VerificationProof } from "@/verification";

/**
 * Discriminated union of how a position's source was verified.
 *
 * EOA: the signer wallet IS the position source.
 * Multisig (Squads V4): position source is the multisig PDA; the
 * signer is a member of that multisig that can verify (Vote or Execute permission).
 */
export type VerifiedSource =
    | {
          /** Address whose control must be established by the issuer. No cryptographic classification is asserted. */
          kind: "manual";
          address: string;
          verificationMode: "direct-solomon";
          proof?: undefined;
      }
    | {
          kind: "eoa";
          address: string;
          verificationMode?: "fresh";
          proof: VerificationProof;
      }
    | {
          kind: "eoa";
          address: string;
          verificationMode: "saved-wallet" | "direct-solomon";
          proof?: undefined;
      }
    | {
          kind: "multisig-squads";
          multisigAddress: string;
          signerAddress: string;
          multisigProgramId: string;
          /** Squads vault PDA — where the multisig's funds live. Optional
           *  because older preflight responses / saved wallets may not
           *  carry it. */
          vaultAddress?: string;
          verificationMode?: "fresh";
          proof: VerificationProof;
      }
    | {
          kind: "multisig-squads";
          multisigAddress: string;
          signerAddress?: string;
          multisigProgramId?: string;
          vaultAddress?: string;
          verificationMode: "saved-wallet" | "direct-solomon";
          proof?: undefined;
      };

export type PositionSourceKind = VerifiedSource["kind"];

/** Address the position lives at (= walletAddress submitted to the API). */
export function sourceAddress(source: VerifiedSource): string {
    return source.kind !== "multisig-squads" ? source.address : source.multisigAddress;
}

/** Address that signed the verification message. */
export function signerAddress(source: VerifiedSource): string {
    return source.kind !== "multisig-squads" ? source.address : (source.signerAddress ?? source.multisigAddress);
}

/**
 * Address the rewards destination should default to for this source.
 *
 * EOA: the wallet itself (signer === source).
 * Multisig: the Squads vault PDA, where the position's funds live, so
 * streamed rewards accumulate alongside the principal. Falls back to the
 * signer wallet when the vault is unknown (e.g. an older saved wallet) —
 * that is the spendable address we can always be sure of.
 */
export function defaultRewardsDestinationAddress(source: VerifiedSource): string {
    if (source.kind !== "multisig-squads") return source.address;
    return source.vaultAddress ?? signerAddress(source);
}
